import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import * as ort from 'onnxruntime-web';

const DATA = '/blog/comma-compression/data';

// --- Types --------------------------------------------------------------

interface LatentsJSON {
  n_pairs: number;
  latent_dim: number;
  mins: number[];
  maxs: number[];
  means: number[];
  scale: number[];
  offset: number[];
  values: number[][]; // [n_pairs][latent_dim], uint16 quantized
}

// --- Styled components --------------------------------------------------

const Wrapper = styled.div`
  margin: 2.5rem 0;
  padding: 1.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg-alt);
`;

const TopGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 1.25rem;
  align-items: start;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
`;

const PanelLabel = styled.div`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  text-transform: uppercase;
`;

const ImgFrame = styled.div`
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  background: #000;
  aspect-ratio: 512 / 384;
  width: 100%;
`;

const FrameCanvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
  image-rendering: auto;
`;

const FrameOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(0, 0, 0, 0.55);
  pointer-events: none;
  letter-spacing: 0.04em;
`;

const FrameToggle = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-top: 0.25rem;
`;

const ToggleButton = styled.button<{ active?: boolean }>`
  font-family: var(--font-mono);
  font-size: 0.65rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.3rem 0.7rem;
  border: 1px solid var(--border);
  background: ${({ active }) => (active ? 'var(--accent)' : 'transparent')};
  color: ${({ active }) => (active ? '#fff' : 'var(--text-muted)')};
  border-radius: 3px;
  cursor: pointer;

  &:hover {
    border-color: var(--accent);
    color: ${({ active }) => (active ? '#fff' : 'var(--accent)')};
  }
`;

const SliderColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  max-height: 384px;
  overflow-y: auto;
  padding-right: 0.4rem;
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 0.6rem 0.75rem;
  background: var(--bg);

  @media (max-width: 820px) {
    max-height: none;
  }
`;

const SliderRow = styled.div`
  display: grid;
  grid-template-columns: 2.5em 1fr 3.6em;
  gap: 0.55rem;
  align-items: center;
`;

const SliderLabel = styled.span`
  font-family: var(--font-mono);
  font-size: 0.65rem;
  color: var(--text-muted);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const SliderValue = styled.span`
  font-family: var(--font-mono);
  font-size: 0.65rem;
  color: var(--text);
  text-align: right;
  white-space: nowrap;
`;

const StyledSlider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 3px;
  background: var(--border);
  border-radius: 2px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    background: var(--accent);
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid var(--bg);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    background: var(--accent);
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid var(--bg);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.85rem;
`;

const UtilityButton = styled.button`
  font-family: var(--font-mono);
  font-size: 0.68rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.4rem 0.8rem;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--text);
  border-radius: 3px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const StripWrap = styled.div`
  margin-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const StripCanvas = styled.canvas`
  width: 100%;
  height: 60px;
  display: block;
  cursor: crosshair;
  border: 1px solid var(--border);
  border-radius: 3px;
  background: #000;
`;

const StatusLine = styled.div`
  font-family: var(--font-mono);
  font-size: 0.65rem;
  color: var(--text-muted);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Caption = styled.div`
  font-family: var(--font-body);
  font-size: 0.82rem;
  color: var(--text-muted);
  line-height: 1.55;
  margin-top: 1rem;
`;

const ErrorBox = styled.div`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--accent);
  padding: 1rem;
  border: 1px solid var(--accent);
  border-radius: 4px;
  background: var(--bg);
`;

// --- Helpers ------------------------------------------------------------

const FRAME_W = 512;
const FRAME_H = 384;
const STRIP_H = 30; // canvas pixel height
const STRIP_W = 600; // canvas pixel width (one column per pair)

function unquantize(values: number[], scale: number[], offset: number[]): number[] {
  const out = new Array(values.length);
  for (let j = 0; j < values.length; j++) {
    out[j] = values[j] * scale[j] + offset[j];
  }
  return out;
}

// Box-Muller for gaussian samples
function gaussian(mean = 0, std = 1): number {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  return mean + std * z;
}

// Render a (3, H, W) Float32 chunk (values 0..255) into ImageData on a canvas.
function tensorToCanvas(
  ctx: CanvasRenderingContext2D,
  rgb: Float32Array,
  channelStride: number,
  pixelOffset: number,
  w: number,
  h: number
) {
  const img = ctx.createImageData(w, h);
  const data = img.data;
  const rOff = pixelOffset + 0 * channelStride;
  const gOff = pixelOffset + 1 * channelStride;
  const bOff = pixelOffset + 2 * channelStride;
  for (let i = 0; i < channelStride; i++) {
    const r = rgb[rOff + i];
    const g = rgb[gOff + i];
    const b = rgb[bOff + i];
    const di = i * 4;
    data[di + 0] = r < 0 ? 0 : r > 255 ? 255 : r | 0;
    data[di + 1] = g < 0 ? 0 : g > 255 ? 255 : g | 0;
    data[di + 2] = b < 0 ? 0 : b > 255 ? 255 : b | 0;
    data[di + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
}

// Build a nice diverging colormap (blue -> white -> red) for the strip.
function valueToColor(t: number): [number, number, number] {
  // t in [0, 1]
  const x = Math.max(0, Math.min(1, t));
  if (x < 0.5) {
    const k = x / 0.5;
    return [Math.round(40 + k * (240 - 40)), Math.round(70 + k * (240 - 70)), Math.round(120 + k * (240 - 120))];
  }
  const k = (x - 0.5) / 0.5;
  return [Math.round(240 - k * (240 - 200)), Math.round(240 - k * (240 - 60)), Math.round(240 - k * (240 - 50))];
}

// --- Component ----------------------------------------------------------

const LiveDecoder: React.FC = () => {
  const [latents, setLatents] = useState<LatentsJSON | null>(null);
  const [sliderValues, setSliderValues] = useState<number[] | null>(null);
  const [pairIndex, setPairIndex] = useState(0);
  const [frameSlot, setFrameSlot] = useState<0 | 1>(1); // 1 = SegNet-supervised
  const [error, setError] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [inferring, setInferring] = useState(false);
  const [lastInferMs, setLastInferMs] = useState<number | null>(null);

  const sessionRef = useRef<ort.InferenceSession | null>(null);
  const frameCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const stripCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pendingRef = useRef<number | null>(null);
  const inflightRef = useRef<boolean>(false);
  const queuedLatentRef = useRef<Float32Array | null>(null);

  // Load latents JSON.
  useEffect(() => {
    let cancelled = false;
    fetch(`${DATA}/latents.json`)
      .then((r) => {
        if (!r.ok) throw new Error(`latents.json: ${r.status}`);
        return r.json();
      })
      .then((j: LatentsJSON) => {
        if (cancelled) return;
        setLatents(j);
        // Initialize sliders to the first trained latent.
        const v = unquantize(j.values[0], j.scale, j.offset);
        setSliderValues(v);
      })
      .catch((e) => {
        if (!cancelled) setError(`Failed to load latents: ${e.message || e}`);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the ONNX session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Configure WASM backend. Default CDN works for dev.
        try {
          // Limit threads to a small number; the model is tiny.
          // (ort.env.wasm exists at runtime even if the type defs vary across versions.)
          (ort.env.wasm as { numThreads?: number }).numThreads = 1;
        } catch {
          /* no-op */
        }
        const session = await ort.InferenceSession.create(`${DATA}/decoder.onnx`, {
          executionProviders: ['wasm'],
          graphOptimizationLevel: 'all',
        });
        if (cancelled) return;
        sessionRef.current = session;
        setSessionReady(true);
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          setError(`Failed to load decoder: ${msg}`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The actual inference call. Runs the latest queued latent; chains follow-up runs if newer values arrived.
  const runInference = useCallback(async () => {
    const session = sessionRef.current;
    if (!session) return;
    if (inflightRef.current) return; // already running; will pick up queued latent on completion

    const next = queuedLatentRef.current;
    if (!next) return;
    queuedLatentRef.current = null;

    inflightRef.current = true;
    setInferring(true);
    const t0 =
      typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();

    try {
      const inputName = session.inputNames[0];
      const outputName = session.outputNames[0];
      const inputTensor = new ort.Tensor('float32', next, [1, next.length]);
      const feeds: Record<string, ort.Tensor> = {};
      feeds[inputName] = inputTensor;
      const out = await session.run(feeds);
      const tensor = out[outputName];
      const data = tensor.data as Float32Array;

      // Output shape: (1, 2, 3, 384, 512)
      const ctx = frameCanvasRef.current?.getContext('2d');
      if (ctx) {
        const channelStride = FRAME_H * FRAME_W; // per channel
        const frameStride = 3 * channelStride; // per frame (3 channels)
        const slot = frameSlotRef.current;
        const pixelOffset = slot * frameStride;
        tensorToCanvas(ctx, data, channelStride, pixelOffset, FRAME_W, FRAME_H);
      }

      const t1 =
        typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      setLastInferMs(t1 - t0);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`Inference failed: ${msg}`);
    } finally {
      inflightRef.current = false;
      setInferring(false);
      // If a newer latent arrived during inference, schedule another run.
      if (queuedLatentRef.current) {
        // Use rAF to yield back to the browser briefly.
        if (typeof requestAnimationFrame !== 'undefined') {
          requestAnimationFrame(() => runInference());
        } else {
          runInference();
        }
      }
    }
  }, []);

  // Track frameSlot in a ref so runInference always reads the latest without re-creating.
  const frameSlotRef = useRef<0 | 1>(frameSlot);
  useEffect(() => {
    frameSlotRef.current = frameSlot;
  }, [frameSlot]);

  // Whenever sliders change, schedule inference (debounced via rAF coalescing).
  useEffect(() => {
    if (!sessionReady || !sliderValues || !latents) return;
    const f32 = new Float32Array(sliderValues);
    queuedLatentRef.current = f32;

    if (pendingRef.current !== null) {
      cancelAnimationFrame(pendingRef.current);
    }
    pendingRef.current = requestAnimationFrame(() => {
      pendingRef.current = null;
      runInference();
    });

    return () => {
      if (pendingRef.current !== null) {
        cancelAnimationFrame(pendingRef.current);
        pendingRef.current = null;
      }
    };
  }, [sliderValues, sessionReady, latents, runInference]);

  // When the user toggles between frame 0 and frame 1, re-run inference (the cached
  // run was for the other slot — easier to just re-decode than to cache outputs).
  useEffect(() => {
    if (!sessionReady || !sliderValues) return;
    queuedLatentRef.current = new Float32Array(sliderValues);
    if (pendingRef.current !== null) cancelAnimationFrame(pendingRef.current);
    pendingRef.current = requestAnimationFrame(() => {
      pendingRef.current = null;
      runInference();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameSlot, sessionReady]);

  // Render the latent-strip visualization once latents arrive.
  useEffect(() => {
    if (!latents) return;
    const cnv = stripCanvasRef.current;
    if (!cnv) return;
    cnv.width = STRIP_W;
    cnv.height = STRIP_H;
    const ctx = cnv.getContext('2d');
    if (!ctx) return;
    const { values, mins, maxs, latent_dim, n_pairs } = latents;
    const img = ctx.createImageData(STRIP_W, STRIP_H);
    const buf = img.data;
    for (let x = 0; x < STRIP_W; x++) {
      const pi = Math.min(n_pairs - 1, Math.floor((x / STRIP_W) * n_pairs));
      const row = values[pi];
      for (let y = 0; y < STRIP_H; y++) {
        // Map strip-y to latent dim.
        const dim = Math.min(latent_dim - 1, Math.floor((y / STRIP_H) * latent_dim));
        const q = row[dim] / 65534;
        const _v = mins[dim] + q * (maxs[dim] - mins[dim]);
        // Normalize within dimension to [0,1] for the colormap.
        const t = (_v - mins[dim]) / Math.max(1e-9, maxs[dim] - mins[dim]);
        const [r, g, b] = valueToColor(t);
        const di = (y * STRIP_W + x) * 4;
        buf[di + 0] = r;
        buf[di + 1] = g;
        buf[di + 2] = b;
        buf[di + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  }, [latents]);

  // Draw the current pair-index indicator on the strip (overlay layer would be cleaner, but
  // a quick re-stroke each render keeps things simple).
  useEffect(() => {
    if (!latents) return;
    const cnv = stripCanvasRef.current;
    if (!cnv) return;
    const ctx = cnv.getContext('2d');
    if (!ctx) return;

    // Re-render base strip first.
    const { values, mins, maxs, latent_dim, n_pairs } = latents;
    const img = ctx.createImageData(STRIP_W, STRIP_H);
    const buf = img.data;
    for (let x = 0; x < STRIP_W; x++) {
      const pi = Math.min(n_pairs - 1, Math.floor((x / STRIP_W) * n_pairs));
      const row = values[pi];
      for (let y = 0; y < STRIP_H; y++) {
        const dim = Math.min(latent_dim - 1, Math.floor((y / STRIP_H) * latent_dim));
        const q = row[dim] / 65534;
        const _v = mins[dim] + q * (maxs[dim] - mins[dim]);
        const t = (_v - mins[dim]) / Math.max(1e-9, maxs[dim] - mins[dim]);
        const [r, g, b] = valueToColor(t);
        const di = (y * STRIP_W + x) * 4;
        buf[di + 0] = r;
        buf[di + 1] = g;
        buf[di + 2] = b;
        buf[di + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);

    // Draw the indicator stripe.
    const x = Math.floor((pairIndex / n_pairs) * STRIP_W);
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fillRect(x, 0, 1, STRIP_H);
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(x - 1, 0, 1, STRIP_H);
    ctx.fillRect(x + 1, 0, 1, STRIP_H);
  }, [latents, pairIndex]);

  const onStripClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!latents) return;
      const cnv = stripCanvasRef.current;
      if (!cnv) return;
      const rect = cnv.getBoundingClientRect();
      const xCss = e.clientX - rect.left;
      const frac = Math.max(0, Math.min(1, xCss / rect.width));
      const pi = Math.min(latents.n_pairs - 1, Math.floor(frac * latents.n_pairs));
      setPairIndex(pi);
      const v = unquantize(latents.values[pi], latents.scale, latents.offset);
      setSliderValues(v);
    },
    [latents]
  );

  const onSliderChange = (idx: number, value: number) => {
    setSliderValues((prev) => {
      if (!prev) return prev;
      const next = prev.slice();
      next[idx] = value;
      return next;
    });
  };

  const onMeanLatent = () => {
    if (!latents) return;
    setSliderValues(latents.means.slice());
  };

  const onRandomNoise = () => {
    if (!latents) return;
    // Small Gaussian noise centered on per-dim midpoints, scaled by per-dim ranges.
    const v = new Array(latents.latent_dim);
    for (let j = 0; j < latents.latent_dim; j++) {
      const mid = (latents.mins[j] + latents.maxs[j]) / 2;
      const range = latents.maxs[j] - latents.mins[j];
      const sample = mid + gaussian(0, 0.25) * range;
      // Clamp to slider range.
      v[j] = Math.max(latents.mins[j], Math.min(latents.maxs[j], sample));
    }
    setSliderValues(v);
  };

  // --- Render -----------------------------------------------------------

  if (error) {
    return (
      <Wrapper>
        <ErrorBox>
          {error}
          <div style={{ marginTop: '0.5rem', color: 'var(--text-muted)' }}>
            The live decoder needs WebAssembly and the ability to fetch ~950 KB of model
            data. Try a recent desktop browser.
          </div>
        </ErrorBox>
      </Wrapper>
    );
  }

  const ready = sessionReady && latents != null && sliderValues != null;
  const dim = latents?.latent_dim ?? 0;

  return (
    <Wrapper>
      <TopGrid>
        <Panel>
          <PanelLabel>Decoded frame (512 × 384, frame {frameSlot} of pair)</PanelLabel>
          <ImgFrame>
            <FrameCanvas
              ref={frameCanvasRef}
              width={FRAME_W}
              height={FRAME_H}
              aria-label="Decoded frame"
            />
            {!ready && (
              <FrameOverlay>
                {sessionReady ? 'Loading latents...' : 'Loading decoder (~950 KB)...'}
              </FrameOverlay>
            )}
          </ImgFrame>
          <FrameToggle>
            <ToggleButton active={frameSlot === 0} onClick={() => setFrameSlot(0)}>
              Frame 0 (junk half)
            </ToggleButton>
            <ToggleButton active={frameSlot === 1} onClick={() => setFrameSlot(1)}>
              Frame 1 (SegNet)
            </ToggleButton>
          </FrameToggle>
          <ButtonRow>
            <UtilityButton onClick={onMeanLatent} disabled={!ready}>
              Mean latent
            </UtilityButton>
            <UtilityButton onClick={onRandomNoise} disabled={!ready}>
              Random noise
            </UtilityButton>
          </ButtonRow>
          <StatusLine style={{ marginTop: '0.6rem' }}>
            <span>Pair {pairIndex} / {(latents?.n_pairs ?? 600) - 1}</span>
            <span>
              {inferring ? 'Decoding...' : lastInferMs != null ? `${lastInferMs.toFixed(0)} ms` : ' '}
            </span>
          </StatusLine>
        </Panel>

        <Panel>
          <PanelLabel>Latent dimensions ({dim})</PanelLabel>
          <SliderColumn>
            {latents && sliderValues
              ? sliderValues.map((v, i) => {
                  const lo = latents.mins[i];
                  const hi = latents.maxs[i];
                  const step = Math.max(1e-6, (hi - lo) / 200);
                  return (
                    <SliderRow key={i}>
                      <SliderLabel>z{i.toString().padStart(2, '0')}</SliderLabel>
                      <StyledSlider
                        type="range"
                        min={lo}
                        max={hi}
                        step={step}
                        value={v}
                        onChange={(e) => onSliderChange(i, parseFloat(e.target.value))}
                        aria-label={`Latent dimension ${i}`}
                      />
                      <SliderValue>{v.toFixed(2)}</SliderValue>
                    </SliderRow>
                  );
                })
              : Array.from({ length: 28 }).map((_, i) => (
                  <SliderRow key={i}>
                    <SliderLabel>z{i.toString().padStart(2, '0')}</SliderLabel>
                    <StyledSlider type="range" min={0} max={1} value={0} disabled readOnly />
                    <SliderValue>·</SliderValue>
                  </SliderRow>
                ))}
          </SliderColumn>
        </Panel>
      </TopGrid>

      <StripWrap>
        <PanelLabel>All 600 trained latents · click to load</PanelLabel>
        <StripCanvas
          ref={stripCanvasRef}
          onClick={onStripClick}
          aria-label="Latent strip; click to pick a pair"
        />
        <StatusLine>
          <span>x = pair index (0 → 599)</span>
          <span>y = latent dim (0 → 27)</span>
          <span>color = value within dim</span>
        </StatusLine>
      </StripWrap>

      <Caption>
        The decoder weights are 159 KB. The 600 latents are a combined 16 KB. Most of the
        road structure is embedded in the weights themselves; the latents pick which
        moment to render. Drag a slider to see how a single dimension changes the output.
        Pick noise as the latent (button), or pick the average of all latents (button), and
        the decoder still produces a road-shaped image because the prior is in the weights.
      </Caption>
    </Wrapper>
  );
};

export default LiveDecoder;
